import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { inCallSegmentForMode, joinWindowFor } from "@/utils/meeting";

// Same vocabulary the table uses. Keys must match the lowercase
// Meeting.status enum values from the server (`pending`, `completed`,
// `cancelled`, `expired`). The previous capitalised string match
// ("Completed", "Pending") never hit any branch and silently fell
// through to the red "cancelled" style for every row.
const statusPillClass = {
  completed: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  expired: "bg-gray-100 text-gray-700",
};

const formatDuration = (durationInSeconds) => {
  const totalSeconds = parseInt(durationInSeconds, 10);

  if (totalSeconds === 0) {
    return "0 min 0 sec";
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes} min ${seconds} sec`;
};

const AppointmentGrid = ({ paginatedData }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full p-4">
      {paginatedData.map((appointment) => {
        const { canJoin, hint } = joinWindowFor(
          appointment.date,
          appointment.status
        );
        const disabledTitle = !appointment.meetingId
          ? "Meeting link unavailable. Contact support."
          : hint || "";
        return (
        <div
          key={appointment.id}
          className="border rounded-xl p-4 shadow-lg bg-white"
        >
          {/* Header Section */}
          <div className="flex justify-between items-center bg-gray-50 shadow-[inset_0_-1px_5px_rgba(10,10,10,0.1)] p-1 rounded-full">
            <p
              className="text-sm font-bold text-gray-600 ml-1"
              title={appointment.reference}
            >
              {/* Mongo ObjectId is 24 chars; show the last 8 like the
                  details page uses. The full id is on title hover. */}
              Ref {String(appointment.reference || "").slice(-8)}
            </p>
            <span
              className={`px-2 py-1 text-xs font-bold rounded-full ${
                statusPillClass[appointment.status] ||
                "bg-gray-100 text-gray-700"
              }`}
            >
              {appointment.status}
            </span>
          </div>

          {/* User Info */}
          <div className="flex items-center mt-4">
            <div className="p-1 border-orange-400 border rounded-full">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-300 text-white font-bold rounded-full">
                {appointment.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-orange-500">
                {appointment.name}
              </h3>
              <p className="text-sm text-gray-500">
                <strong className="text-orange-500">Type:</strong>{" "}
                {appointment.type}
              </p>
              <p className="text-sm text-gray-500">
                <strong className="text-orange-500">Mode:</strong>{" "}
                {appointment.mode}
              </p>
            </div>
          </div>

          {/* Details */}
          <div className="flex justify-evenly mt-4 text-gray-600 text-sm rounded-3xl shadow-[inset_0_-1px_5px_rgba(10,10,10,0.1)] py-2 px-5">
            <div className="border-r border-gray-300 pr-5">
              <p className="font-bold text-lg text-red-600">
                {(appointment.status === "pending" || appointment.status === "cancelled") ? formatDuration(0) : formatDuration(appointment.duration)}
              </p>
              <p>Duration</p>
            </div>
            <div className="border-r border-gray-300 pr-5">
              <p className="font-bold text-lg">{appointment.rate}</p>
              <p>Rate/Min</p>
            </div>
            <div>
              {/* Pending/cancelled rows have no real earning yet — show
                  a dash rather than a blank tile or a phantom ₹0. */}
              <p className="font-bold text-lg">
                {appointment.earning || "—"}
              </p>
              <p>Earning</p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex justify-between gap-2">
            <Link
              to={`/schedule/appointments/details/${appointment.id}`}
              className="flex-1"
            >
              <button className="w-full px-4 py-2 text-sm font-bold hover:bg-gray-300 rounded-full bg-gray-100">
                View Details
              </button>
            </Link>
            {canJoin && appointment.meetingId ? (
              <Link
                to={`/connect/schedule/${inCallSegmentForMode(
                  appointment.mode
                )}/${appointment.meetingId}`}
                className="flex-1"
              >
                <button className="w-full px-4 py-2 text-sm font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-full">
                  Start Consultation
                </button>
              </Link>
            ) : (
              <button
                disabled
                className="flex-1 px-4 py-2 text-sm font-bold text-white bg-orange-300 rounded-full cursor-not-allowed"
                title={disabledTitle}
              >
                Start Consultation
              </button>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default AppointmentGrid;

AppointmentGrid.propTypes = {
  paginatedData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      reference: PropTypes.string,
      type: PropTypes.string.isRequired,
      mode: PropTypes.string.isRequired,
      // moment().toDate() in the index.jsx mapper returns a Date object.
      date: PropTypes.instanceOf(Date).isRequired,
      status: PropTypes.oneOf(["completed", "pending", "cancelled", "expired"])
        .isRequired,
      duration: PropTypes.number.isRequired,
      rate: PropTypes.string.isRequired,
      meetingId: PropTypes.string.isRequired,
      // earning is an estimate string for completed meetings; null/empty
      // for pending/cancelled.
      earning: PropTypes.string,
    })
  ).isRequired,
};
